import { useMemo } from 'react';
import {
  SessionMinutesDto,
  SessionSensorUtilizationDto,
  TopExperienceDataDto,
  SessionTrendDataDto,
  SessionDataDashboardDto,
  MonthlySessionDetailDto,
  ExperienceSessionDetailDto,
} from '@repo/shared/dtos/analytics/AnalyticsResponseDto';
import { SessionsByExperienceDto } from '@repo/shared/dtos/analytics/SessionDataDto';
import {
  BaseAnalyticsFilter,
  SessionDetailFilter,
} from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { useAnalyticsQuery } from './useAnalyticsQuery';
import { BASE_URLS, DEFAULTS } from './config';
import { normalizeAnalyticsFilters, createDefaultBaseFilters, formatSessionMetrics } from './utils';
import { ANALYTICS_CONSTANTS } from '../../components/analytics/constants';

// Re-export extracted hooks so existing import paths continue to work
export { useSessionLog } from './useSessionLog';
export type { SessionLogResult, SessionLogOptions, SessionLogStatus } from './useSessionLog';
export { useDashboardAnalytics } from './useDashboardAnalytics';

const sessionAnalyticsConfig = {
  baseUrl: BASE_URLS.SESSIONS,
  queryKeyPrefix: 'analytics',
};

export const useAnalytics = <T>(
  endpoint: string,
  filters?: BaseAnalyticsFilter,
  enabled = true,
) => {
  const normalizedFilters = normalizeAnalyticsFilters(filters, createDefaultBaseFilters());
  const queryConfig = useMemo(() => ({ ...sessionAnalyticsConfig, enabled }), [enabled]);

  return useAnalyticsQuery<T>(endpoint, normalizedFilters, queryConfig);
};

export const useSessionKpis = (filters?: BaseAnalyticsFilter) =>
  useAnalytics<SessionMinutesDto>('/minutes', filters);

export const useSessionSensorUtilization = (filters?: BaseAnalyticsFilter) =>
  useAnalytics<SessionSensorUtilizationDto>('/sensor-utilization', filters);

export const useSessionTrends = (filters?: BaseAnalyticsFilter) => {
  const granularity: 'month' | 'day' = (() => {
    if (filters?.granularity) return filters.granularity;
    if (filters?.startDate && filters?.endDate) {
      const diffMs = Math.abs(filters.endDate.getTime() - filters.startDate.getTime());
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      return diffDays <= ANALYTICS_CONSTANTS.MAX_DAILY_RANGE_DAYS ? 'day' : 'month';
    }
    return 'month';
  })();

  const normalizedFilters = normalizeAnalyticsFilters(
    { ...filters, granularity },
    createDefaultBaseFilters(),
  );

  return useAnalyticsQuery<SessionTrendDataDto[]>(
    '/trends',
    normalizedFilters,
    sessionAnalyticsConfig,
  );
};

export const useSessionsByExperience = (filters?: BaseAnalyticsFilter) =>
  useAnalytics<SessionsByExperienceDto[]>('/by-experience', filters);

export const useDashboardData = (filters?: BaseAnalyticsFilter) =>
  useAnalytics<SessionDataDashboardDto[]>('/dashboard', filters);

export const useTopExperiences = (filters?: BaseAnalyticsFilter, limit?: number) => {
  const actualLimit = String(limit ?? DEFAULTS.TOP_EXPERIENCES_LIMIT);
  const normalizedFilters = normalizeAnalyticsFilters(filters, createDefaultBaseFilters());
  return useAnalyticsQuery<TopExperienceDataDto[]>(
    '/top-experiences',
    normalizedFilters,
    sessionAnalyticsConfig,
    { limit: actualLimit },
  );
};

export const useSessionMinutes = (filters?: BaseAnalyticsFilter) => {
  const query = useAnalytics<SessionMinutesDto>('/minutes', filters);

  const computed = useMemo(() => {
    if (!query.data) return null;

    const current = {
      totalSessions: query.data.totalSessionsCurrentMonth || 0,
      totalMinutes: query.data.totalMinutesCurrentMonth || 0,
    };

    const allTime = {
      totalSessions: query.data.totalSessionsAllTime || 0,
      totalMinutes: query.data.totalMinutesAllTime || 0,
    };

    const currentMetrics = formatSessionMetrics(current);
    const allTimeMetrics = formatSessionMetrics(allTime);

    return {
      ...current,
      totalSessionsAllTime: allTime.totalSessions,
      totalMinutesAllTime: allTime.totalMinutes,
      ...currentMetrics,
      averageSessionLengthAllTime: allTimeMetrics.averageSessionLength,
      formattedDurationAllTime: allTimeMetrics.formattedDuration,
    };
  }, [query.data]);

  return { ...query, computed };
};

export const useMonthlySessionDetails = (
  year: number,
  month: number,
  filters?: SessionDetailFilter,
) => {
  return useAnalytics<MonthlySessionDetailDto>(`/details/monthly/${year}/${month}`, filters);
};

export const useExperienceSessionDetails = (
  experienceTitle: string,
  filters?: SessionDetailFilter,
  enabled = true,
) => {
  return useAnalytics<ExperienceSessionDetailDto>(
    `/details/experience/${encodeURIComponent(experienceTitle)}`,
    filters,
    enabled,
  );
};
